import React from 'react';
import { useDroppable, useDndContext } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Box, Typography, Chip, IconButton } from '@mui/material';
import { Delete as DeleteIcon, DragIndicator } from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ComponentPreview from './ComponentPreview';
import { SIMPLE_OPERATORS } from '../../utils/conditionUtils';

function conditionSummary(condition) {
  if (!condition) return 'No condition';
  if (condition.mode === 'power') {
    const expr = condition.expression?.trim() || '';
    return expr.length > 40 ? expr.slice(0, 40) + '…' : expr || 'No condition';
  }
  if (condition.field) {
    const op = SIMPLE_OPERATORS.find(o => o.value === condition.operator);
    return `${condition.field} ${op?.expr || ''} ${condition.value || ''}`.trim();
  }
  return 'No condition';
}

function BranchDropZone({ parentId, branchKey, index }) {
  const { active } = useDndContext();
  const isDraggingFromPalette = active?.id?.startsWith('palette-');

  const { setNodeRef, isOver } = useDroppable({
    id: `branch-dz-${parentId}-${branchKey}-${index}`,
    data: { type: 'branch-drop-zone', parentId, branchKey, index },
    disabled: !isDraggingFromPalette,
  });

  const shouldShow = isDraggingFromPalette;
  const isHighlighted = isOver && isDraggingFromPalette;

  return (
    <Box
      ref={setNodeRef}
      sx={{
        minHeight: shouldShow ? (isHighlighted ? 28 : 8) : 2,
        border: isHighlighted ? '2px dashed' : (shouldShow ? '1px dashed #e0e0e0' : 'none'),
        borderColor: isHighlighted ? 'primary.main' : undefined,
        borderRadius: 1,
        backgroundColor: isHighlighted ? 'rgba(79, 70, 229, 0.1)' : 'transparent',
        margin: shouldShow ? '3px 0' : '1px 0',
        transition: 'all 0.15s ease',
        opacity: shouldShow ? 1 : 0,
        pointerEvents: isDraggingFromPalette ? 'auto' : 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {isHighlighted && (
        <Typography variant="caption" color="primary" sx={{ fontSize: 10, fontWeight: 500 }}>
          Drop here
        </Typography>
      )}
    </Box>
  );
}

function BranchZone({ parentId, branchKey, label, labelColor, items, selectedComponentId, onSelectNested, onDeleteNested }) {
  const { active } = useDndContext();
  const isDraggingFromPalette = active?.id?.startsWith('palette-');

  const { setNodeRef, isOver } = useDroppable({
    id: `branch-zone-${parentId}-${branchKey}`,
    data: { type: 'branch-zone', parentId, branchKey },
  });

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color: labelColor, fontSize: '0.73rem', px: 0.5, display: 'block', mb: 0.25 }}
      >
        {label}
      </Typography>
      <Box
        ref={setNodeRef}
        sx={{
          minHeight: 28,
          borderRadius: 1,
          border: isOver && isDraggingFromPalette && items.length === 0
            ? '2px dashed'
            : '1px solid rgba(0,0,0,0.10)',
          borderColor: isOver && isDraggingFromPalette && items.length === 0
            ? 'primary.main'
            : undefined,
          backgroundColor: isOver && isDraggingFromPalette && items.length === 0
            ? 'rgba(79, 70, 229, 0.05)'
            : 'rgba(0,0,0,0.015)',
          p: 0.5,
          transition: 'all 0.15s ease',
        }}
      >
        <SortableContext items={items.map(c => c.id)} strategy={verticalListSortingStrategy}>
          {items.length === 0 ? (
            <BranchDropZone parentId={parentId} branchKey={branchKey} index={0} />
          ) : (
            <>
              <BranchDropZone parentId={parentId} branchKey={branchKey} index={0} />
              {items.map((comp, idx) => (
                <React.Fragment key={comp.id}>
                  <BranchItem
                    comp={comp}
                    parentId={parentId}
                    branchKey={branchKey}
                    isSelected={selectedComponentId === comp.id}
                    onSelect={(e) => { e.stopPropagation(); onSelectNested(comp); }}
                    onDelete={() => onDeleteNested(comp.id)}
                  />
                  <BranchDropZone parentId={parentId} branchKey={branchKey} index={idx + 1} />
                </React.Fragment>
              ))}
            </>
          )}
        </SortableContext>
      </Box>
    </Box>
  );
}

function BranchItem({ comp, parentId, branchKey, isSelected, onSelect, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: comp.id,
    data: { type: 'branch-sortable', component: comp, parentId, branchKey },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      onClick={onSelect}
      sx={{
        position: 'relative',
        border: '1px solid',
        borderColor: isSelected ? 'primary.main' : 'rgba(0,0,0,0.12)',
        borderRadius: 1,
        backgroundColor: isSelected ? 'rgba(79, 70, 229, 0.06)' : 'white',
        p: '4px 6px',
        cursor: 'pointer',
        '&:hover': { borderColor: 'primary.light', backgroundColor: 'rgba(79, 70, 229, 0.03)' },
      }}
    >
      <Chip
        label={comp.type}
        size="small"
        color="primary"
        sx={{ fontSize: '0.58rem', height: 16, position: 'absolute', top: -8, left: 6 }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ComponentPreview component={comp} />
        </Box>
        <Box sx={{ display: 'flex', ml: 0.5, flexShrink: 0 }}>
          <IconButton {...attributes} {...listeners} size="small" sx={{ p: 0.25 }}>
            <DragIndicator sx={{ fontSize: 14 }} />
          </IconButton>
          <IconButton
            size="small"
            sx={{ p: 0.25 }}
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
          >
            <DeleteIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

const ContainerCanvas = ({
  component,
  isSelected,
  onSelect,
  onDelete,
  onSelectNested,
  selectedComponent,
  addComponentToBranch,
  reorderBranchComponents,
  removeComponentFromBranch,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: component.id,
    data: { type: 'sortable-component', component },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isIf = component.type === 'If';
  const props = component.properties;

  let headerLabel;
  let branches;

  if (isIf) {
    const summary = conditionSummary(props._ifCondition);
    headerLabel = `IF ${summary}`;
    branches = [
      { key: 'then', label: 'THEN', color: 'success.main', items: props.then || [] },
      { key: 'else', label: 'ELSE', color: 'text.secondary', items: props.else || [] },
    ];
  } else {
    const val = props._switchValue || '-';
    headerLabel = `SWITCH ${val}`;
    branches = (props.cases || []).map(c => ({
      key: `case:${c.id}`,
      label: c.caseValue ? `case "${c.caseValue}"` : 'New case',
      color: 'secondary.main',
      items: c.children || [],
    }));
    if (branches.length === 0) {
      branches = [{ key: 'case:empty', label: 'Add a case', color: 'text.disabled', items: [] }];
    }
  }

  return (
    <Box
      ref={setNodeRef}
      style={style}
      className={`canvas-component ${isSelected ? 'selected' : ''}`}
      data-testid={`canvas-component-${component.type}`}
      onClick={onSelect}
      sx={{
        borderColor: isSelected ? 'primary.main' : 'transparent',
        backgroundColor: isSelected ? 'rgba(79, 70, 229, 0.05)' : 'white',
        position: 'relative',
      }}
    >
      <Chip
        label={component.type}
        size="small"
        className="canvas-component__type"
        color={isIf ? 'primary' : 'secondary'}
      />

      <Box className="canvas-component__actions">
        <IconButton {...attributes} {...listeners} size="small" className="canvas-component__drag-handle">
          <DragIndicator />
        </IconButton>
        <IconButton
          size="small"
          className="canvas-component__delete"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
        >
          <DeleteIcon />
        </IconButton>
      </Box>

      <Box
        className="canvas-component__content"
        sx={{ flexDirection: 'column', gap: 1, pt: 0.5, pointerEvents: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.68rem', display: 'block' }}>
          {headerLabel}
        </Typography>

        {branches.map(({ key, label, color, items }) => (
          <BranchZone
            key={key}
            parentId={component.id}
            branchKey={key}
            label={label}
            labelColor={color}
            items={items}
            selectedComponentId={selectedComponent?.id}
            onSelectNested={onSelectNested}
            onDeleteNested={(nestedId) => removeComponentFromBranch(component.id, key, nestedId)}
          />
        ))}
      </Box>
    </Box>
  );
};

export default ContainerCanvas;
